/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */
import {
	useState,
	useLayoutEffect,
	useCallback,
	useEffect,
} from 'react';

import { Box, Paper, Group, Image } from '@mantine/core';

import {
	useDisclosure,
	useElementSize,
	useMediaQuery,
} from '@mantine/hooks';

// Styles
import classes from './offsets.module.css';

// Components
import {
	Button,
	LightButton,
	Text,
	AddFiltersButton,
	Tooltip,
	Pagination,
} from '@components/atoms';
import {
	DateFilter,
	MenuFilter,
} from '@components/molecules';
import { Datatable } from '@components/organisms';

// I18n
import { useTranslation } from 'react-i18next';

// Icons
import {
	IconCirclePlus,
	IconDownload,
	IconSend,
} from '@tabler/icons-react';
import ArrowScrollIcon from '@assets/icons/arrow-scroll.svg';

// Apis
import { useBrands } from 'shared/api/get-brands';
import { useProjects } from 'shared/api/get-projects';

// Store
import useStore from '@store/index';

// Datattable config
import { tableSchemaBase as columns } from '@features/offsets/offsets.columns';

// Utils
import {
	convertArrayToLabelValue,
	truncateString,
} from '@utils/functions';

// Hooks
import { useOffsetList } from '@features/offsets/hooks/useOffsetList';

// Constants
import { ROLES } from '@config/constants/roles';

// Authorizations
import {
	Authorization,
	useAuthorization,
} from '@lib/authorization';

// Local components
import { DrawerOffsetShowMore } from '@features/offsets/components/DrawerOffsetShowMore/DrawerOffsetShowMore';
import { RowExpansion } from '@features/offsets/components/RowExpansion';

// Types
import {
	TOffset,
	TOffsetPopulated,
} from '@features/offsets/types';
import { notifications } from '@mantine/notifications';
import dayjs from 'dayjs';
import { TCertificate, TUser } from '@global-types/index';
import findIndex from 'lodash/findIndex';
import { queryClient } from '@lib/react-query';
import { useSendCertificate } from '@features/offsets/api/send-certificate';

import get from 'lodash/get';
import { isNil } from 'lodash';
import { useCreateCertificate } from '@features/offsets/api/create-certificate';
import { useOffsets } from '@features/offsets/api/get-offsets';
import { getCertificate } from '@features/offsets/api/get-certificate';
import { ModalSendCertificate } from '@features/offsets/components/ModalSendCertificate';
import { sendCertificate } from '@features/offsets/api/send-certificate-asynchrenous';
import { ACLS } from '@config/constants/acls';
import { useProducers } from '@features/offsets/api/get-producers';

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */
const Offsets = () => {
	/* ******************************** Media Query ******************************* */
	const isBigScreen = useMediaQuery('(min-width: 120em)');

	const { t } = useTranslation();

	const [showScrollFilters, setShowScrollFilters] =
		useState(false);

	const [expandedIds, setExpandedIds] = useState<
		string[]
	>([]);

	const [isOverflowFilters] = useState(false);
	const [openViewMore, setOpenViewMore] = useState(false);
	const [opened, { open, close }] = useDisclosure(false);

	const {
		offsetsPage,
		offsetsPageSize,
		setOffsetOpenDrawer,
		offsetsQueryParams,
		offsetsSortParams,
		offsetsSortStatus,
		setOffsetsSelectedRow,
		offsetsSelectedRow,
		setOffsetsPage,
		setOffsetsPageSize,
		setOffsetsQueryParams,
		setOffsetsSortParams,
		setOffsetsSortStatus,
		setOffsetsDatatableOptions,
		offsetsDatatableOptions,
		getUser,
	} = useStore();
	const user = getUser();

	// APIs
	const { data: brands } = useBrands();
	const { data: producers } = useProducers();
	const { data: projects } = useProjects();

	const handleScroll = (scrollTo: string) => {
		if (containerRef.current) {
			if (scrollTo === 'right') {
				containerRef.current.scrollLeft =
					containerRef.current.scrollLeft +
					containerRef.current.offsetWidth;
			} else {
				containerRef.current.scrollLeft =
					containerRef.current.scrollLeft -
					containerRef.current.offsetWidth;
			}
		}
	};

	const { ref, width: widthContainer } = useElementSize();

	useLayoutEffect(() => {
		if (
			containerRef.current &&
			containerRef.current.clientWidth <
				containerRef.current.scrollWidth
		) {
			setShowScrollFilters(true);
		} else {
			setShowScrollFilters(false);
		}
	}, [widthContainer]);

	const {
		data,
		isLoading,
		total: totalItems,
		totalPages,
	} = useOffsets(
		offsetsPage,
		offsetsPageSize,
		offsetsQueryParams,
		offsetsSortParams
	);

	const {
		mutate,
		isLoading: isLoadingCreateCertificate,
	} = useCreateCertificate();

	const {
		mutate: mutateSendCertificate,
		isLoading: isLoadingSendCertificate,
	} = useSendCertificate();

	const [
		containerRef,
		,
		,
		handleFilterBrands,
		handleFilterDate,
		handleFilterProducer,
		handleFilterProject,
		handleChangePage,
		handleChangeSortStatus,
		handleChangePageSize,
	] = useOffsetList();

	const handleCloseDrawerShowMore = useCallback(() => {
		setOffsetsSelectedRow({});
		setOpenViewMore(false);
	}, []);

	const handleOpenDrawerShowMore = useCallback(
		(row: TOffset) => {
			setOffsetsSelectedRow(row);
			setOpenViewMore(true);
		},
		[]
	);

	const handleReset = () => {
		setOffsetsPageSize(10);
		setOffsetsPage(1);
		setOffsetsQueryParams({});
		setOffsetsSortParams('-createdAt _id');
		setOffsetsSortStatus({});
	};

	useEffect(() => {
		return () => {
			handleReset();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const getVisibleColumns = (
		columnsConfig: any[],
		columnOrderConfig: any[]
	) => {
		const activeColumns = columnOrderConfig
			.filter((config) => config.checked)
			.map((config) => config.name);

		const visibleColumns = columnsConfig.filter((col) =>
			activeColumns.includes(col.accessor)
		);

		return columnOrderConfig
			.filter((config) => config.checked)
			.map((config) => {
				const column = visibleColumns.find(
					(col) => col.accessor === config.name
				);
				return column || {};
			});
	};

	const handleCloseModalSendCertificate = () => {
		close();
		setOffsetsSelectedRow({});
	};

	const handleDownloadCertificate = async (
		id: string,
		nameOffset: string,
		nameBrand: string,
		contributionDate: string,
		certificateId: string,
		lang: string,
		certificates: TCertificate[]
	) => {
		const idNotification = notifications.show({
			loading: true,
			title: `${t(
				`offsets.notifications.loadingTheCertificate`
			)} ${truncateString(nameOffset, 18)}`,
			message: `${t(
				`offsets.notifications.theDownloadOfCertification`
			)} ${nameOffset} ${t(
				`offsets.notifications.isCurrentlyInProgress`
			)}`,
			autoClose: false,
			withCloseButton: false,
		});

		const parsedContributionDate = dayjs(
			contributionDate,
			'DD/MM/YYYY'
		).format('DD-MM-YYYY');

		let offsetHasCertificate: boolean = false;

		if (certificates && Array.isArray(certificates)) {
			offsetHasCertificate =
				findIndex(certificates, {
					language: lang,
				}) !== -1;
		}

		if (offsetHasCertificate) {
			await getCertificate(
				certificateId,
				nameOffset,
				nameBrand,
				parsedContributionDate,
				idNotification
			);
		} else {
			mutate(
				{
					offset: id,
					language: lang,
				},
				{
					onSuccess: async (response) => {
						await getCertificate(
							response.id,
							nameOffset,
							nameBrand,
							parsedContributionDate,
							idNotification
						);

						queryClient.invalidateQueries({
							queryKey: [
								'offsets',
								{
									page: offsetsPage,
									size: offsetsPageSize,
									queryParams: offsetsQueryParams,
									sortParams: offsetsSortParams,
								},
							],
						});
					},
				}
			);
		}
	};

	const handleSendCertificate = async (data: {
		other?: string;
		recepient: string | null;
		language: string;
	}) => {
		let offsetHasCertificate: boolean = false;

		if (
			offsetsSelectedRow.certificates &&
			Array.isArray(offsetsSelectedRow.certificates)
		) {
			offsetHasCertificate =
				findIndex(offsetsSelectedRow.certificates, {
					language: data.language,
				}) !== -1;
		}

		if (offsetHasCertificate) {
			const certificateFound =
				offsetsSelectedRow.certificates.find(
					(item: TCertificate) =>
						item.language == data.language
				);
			mutateSendCertificate(
				{
					certificateId: certificateFound.id,
					...(data &&
						!isNil(data.recepient) &&
						data.recepient !== 'other' && {
							email: data.recepient,
						}),

					...(data &&
						!isNil(data.recepient) &&
						data.recepient == 'other' &&
						!isNil(data.other) && { email: data.other }),
				},
				{
					onSuccess: async () => {
						close();
					},
				}
			);
		} else {
			mutate(
				{
					offset: offsetsSelectedRow.id,
					language: data.language,
				},
				{
					onSuccess: async (response) => {
						const idNotification = notifications.show({
							loading: true,
							title: t('global.In progress'),
							message: t(
								`notifications.certificateInProgress`
							),
							color: 'success',
							autoClose: false,
							withCloseButton: false,
						});
						const certificateId = response.id;

						await sendCertificate({
							idNotification,
							certificateId: certificateId,
							...(data &&
								!isNil(data.recepient) &&
								data.recepient !== 'other' && {
									email: data.recepient,
								}),

							...(data &&
								!isNil(data.recepient) &&
								data.recepient == 'other' &&
								!isNil(data.other) && {
									email: data.other,
								}),
						});
						handleCloseModalSendCertificate();
						queryClient.invalidateQueries({
							queryKey: [
								'offsets',
								{
									page: offsetsPage,
									size: offsetsPageSize,
									queryParams: offsetsQueryParams,
									sortParams: offsetsSortParams,
								},
							],
						});
					},
				}
			);
		}
	};

	const getRecipientOptions = (
		offsetsSelectedRow: TOffsetPopulated,
		user: TUser
	) => {
		const options = [];

		const IS_ADMIN =
			user.role == ROLES.ADMIN ||
			user.role == ROLES.SUPERADMIN ||
			user.role == ROLES.STAFF;

		const ownerEmail = get(
			offsetsSelectedRow,
			'organizationData.owner.email'
		);

		if (ownerEmail && IS_ADMIN) {
			options.push({
				label:
					`(${t('global.organizationOwner')}) ` +
					ownerEmail,
				value: ownerEmail,
			});
		}

		if (typeof user !== 'boolean' && user?.email) {
			options.push({
				label: `(${t('global.you')}) ` + user.email,
				value: user.email,
			});
		}

		const contactEmail = get(
			offsetsSelectedRow,
			'entity.contact.email'
		);
		if (contactEmail && IS_ADMIN) {
			options.push({
				label:
					`(${t('global.brandContact')}) ` + contactEmail,
				value: contactEmail,
			});
		}
		const uniqueOptions = options.filter(
			(item, index, self) =>
				self.findIndex(
					(el) => el.value === item.value
				) === index
		);

		return uniqueOptions;
	};

	const { checkAcl } = useAuthorization();

	return (
		<Box className={classes.container} ref={ref}>
			{/*View more */}
			<DrawerOffsetShowMore
				handleclose={handleCloseDrawerShowMore}
				opened={openViewMore}
				data={offsetsSelectedRow}
				position={'right'}
			/>

			<Paper className={classes.paper}>
				{/* Row filters */}
				<Box className={classes.containerFilters}>
					<Box className={classes.wrapperRowLeft}>
						{!isOverflowFilters && showScrollFilters && (
							<Box
								className={
									classes.wrapperIconScrollToRight
								}
								variant="transparent"
								onClick={() => {
									handleScroll('left');
								}}
							>
								<Image
									className={
										classes.containerIconScrollToLeft
									}
									src={ArrowScrollIcon}
								/>
							</Box>
						)}
						<Box
							ref={containerRef}
							className={classes.wrapperFilters}
						>
							<Group gap={'0px'}>
								<Box
									className={classes.containerFilters5}
								>
									<Authorization
										allowedRoles={[
											ROLES.ADMIN,
											ROLES.SUPERADMIN,
											ROLES.STAFF,
										]}
									>
										<Box
											className={
												classes.containerFilterSelect
											}
										>
											<MenuFilter
												onApply={handleFilterBrands}
												label={t('global.brand')}
												size="lg"
												options={convertArrayToLabelValue(
													brands
												)}
												placeholderSearch={t(
													'filters.placeholderSearch.searchBrand'
												)}
												placeholder={t(
													'filters.placeholders.selectBrand'
												)}
											/>
										</Box>
										<Box
											className={classes.spacerFilter}
										/>
									</Authorization>
									<Box
										className={
											classes.containerFilterSelect
										}
									>
										<DateFilter
											onApply={handleFilterDate}
										/>
									</Box>
									<Box className={classes.spacerFilter} />
									<Authorization
										allowedRoles={[
											ROLES.ADMIN,
											ROLES.SUPERADMIN,
											ROLES.STAFF,
										]}
									>
										<Box
											className={
												classes.containerFilterSelect
											}
										>
											<MenuFilter
												onApply={handleFilterProducer}
												disabled
												label={t('global.producer')}
												size="lg"
												options={convertArrayToLabelValue(
													producers
												)}
												placeholderSearch={t(
													'filters.placeholderSearch.searchProducer'
												)}
												placeholder={t(
													'filters.placeholders.selectProducer'
												)}
											/>
										</Box>
									</Authorization>
									<Box className={classes.spacerFilter} />
									<Authorization
										allowedRoles={[
											ROLES.ADMIN,
											ROLES.SUPERADMIN,
											ROLES.STAFF,
										]}
									>
										<Box
											className={
												classes.containerFilterSelect
											}
										>
											<MenuFilter
												onApply={handleFilterProject}
												label={t('global.project')}
												disabled
												size="lg"
												options={convertArrayToLabelValue(
													projects
												)}
												placeholderSearch={t(
													'filters.placeholderSearch.selectProject'
												)}
												placeholder={t(
													'filters.placeholders.selectProject'
												)}
											/>
										</Box>
										<Box
											className={classes.spacerFilter}
										/>
										<Box
											className={
												classes.containerFilterNumeric
											}
										>
											<AddFiltersButton>
												{t('global.addFilters')}
											</AddFiltersButton>
										</Box>{' '}
									</Authorization>
								</Box>
							</Group>
						</Box>
						{!isOverflowFilters && showScrollFilters && (
							<Box
								className={
									classes.wrapperIconScrollToRight
								}
								variant="transparent"
								onClick={() => {
									handleScroll('right');
								}}
							>
								<Image
									className={
										classes.containerIconScrollToRight
									}
									src={ArrowScrollIcon}
								/>
							</Box>
						)}
					</Box>
					{checkAcl(ACLS.OFFSETS.CREATE) && (
						<Authorization
							allowedRoles={[
								ROLES.ADMIN,
								ROLES.SUPERADMIN,
								ROLES.STAFF,
							]}
						>
							<div className={classes.wrapperRowRight}>
								<Button
									leftSection={
										<IconCirclePlus size={23} />
									}
									hiddenFrom="xxl"
									size="md"
									onClick={() => {
										setOffsetOpenDrawer(true);
									}}
								>
									{t('offsets.addOffset')}
								</Button>
								<Button
									leftSection={
										<IconCirclePlus size={23} />
									}
									visibleFrom="xxl"
									size="lg"
									onClick={() => {
										setOffsetOpenDrawer(true);
									}}
								>
									{t('offsets.addOffset')}
								</Button>
							</div>{' '}
						</Authorization>
					)}
				</Box>
				<Box className={classes.spacerRow} />
				{/* Row actions */}
				<Authorization
					allowedRoles={[
						ROLES.ADMIN,
						ROLES.SUPERADMIN,
						ROLES.STAFF,
					]}
				>
					<Box className={classes.containerRow}>
						<Group
							gap={'10px'}
							className={classes.containerRowLeft}
						>
							<Tooltip
								label={'0'}
								isActive={false}
								position={'left'}
							>
								<Box
									className={
										classes.containerNbrSelection
									}
								>
									<Text
										type="body2"
										color="inputs-texts-icons"
										size="xs"
									>
										{'('}
										{0}
										{') '}
										{t('global.selected')}
									</Text>
								</Box>
							</Tooltip>
							<LightButton
								disabled
								leftSection={<IconDownload size={18} />}
								size={isBigScreen ? 'lg' : 'md'}
								onClick={() => {}}
							>
								{t('global.downloadCertification')}
							</LightButton>

							<Authorization
								allowedRoles={[
									ROLES.ADMIN,
									ROLES.SUPERADMIN,
									ROLES.STAFF,
								]}
							>
								<LightButton
									disabled
									leftSection={<IconSend size={18} />}
									size={isBigScreen ? 'lg' : 'md'}
									onClick={() => {}}
								>
									{t('global.sendCertification')}
								</LightButton>
							</Authorization>
						</Group>
					</Box>
				</Authorization>
				<Box className={classes.spacerRow} />
				<ModalSendCertificate
					loading={
						isLoadingSendCertificate ||
						isLoadingCreateCertificate
					}
					opened={opened}
					recepientOptions={getRecipientOptions(
						offsetsSelectedRow,
						user
					)}
					handlesubmit={(data: any) => {
						handleSendCertificate(data);
					}}
					handleclose={handleCloseModalSendCertificate}
				/>
				{/* Datatable */}
				<Box className={classes.containerDatatable}>
					<Datatable
						handleSendCertificate={(
							offset: TOffsetPopulated
						) => {
							// Send certificate
							setOffsetsSelectedRow(offset);
							open();
						}}
						handleUpdateColumns={(cols: any[]) => {
							setOffsetsDatatableOptions(
								getVisibleColumns(columns, cols)
							);
						}}
						loading={
							isLoading ? isLoading.toString() : undefined
						}
						allColumns={columns}
						columns={offsetsDatatableOptions}
						data={data}
						onViewMore={handleOpenDrawerShowMore}
						onDownload={handleDownloadCertificate}
						style={{
							minHeight: '100%',
						}}
						height={250}
						selectedRecords={[]}
						onSortStatusChange={handleChangeSortStatus}
						sortstatus={offsetsSortStatus}
						expandedids={expandedIds}
						rowExpansion={{
							expanded: {
								recordIds: expandedIds,
								onRecordIdsChange: setExpandedIds,
							},
							content: (record: { record: TOffset }) => {
								const { id } = record.record;
								return <RowExpansion offsetId={id} />;
							},
						}}
					/>
				</Box>
			</Paper>
			<Box className={classes.containerPagination}>
				<Pagination
					pagesize={offsetsPageSize}
					totalitems={totalItems}
					activepage={offsetsPage}
					handlechangesize={handleChangePageSize}
					onChange={handleChangePage}
					total={totalPages ? totalPages : 0}
				/>
			</Box>
		</Box>
	);
};

export default Offsets;
